import type { Roller } from './dice.ts';

/**
 * RULE DCC-ADV-001 / DCC-ADV-002 / DCC-ADV-003 - Rules for Survival p.14.
 *  - Advantage: roll an extra d20 and keep the highest.
 *  - Disadvantage: roll an extra d20 and keep the lowest.
 *  - Advantage and Disadvantage cancel out; the player rolls normally.
 *  - Two instances of the same one do not combine; still only one extra die.
 */
export type RollMode = 'normal' | 'advantage' | 'disadvantage';

/**
 * Collapses any number of Advantage and Disadvantage sources into one mode.
 *
 * Cancellation is PAIRWISE, not presence-based: Rules for Survival p.14 states
 * that two reasons for Advantage against one for Disadvantage leaves the
 * crawler with Advantage. Sources therefore cancel one-for-one, and whatever
 * remains decides the mode. "Two instances do not combine" refers to the dice:
 * a surplus of two Advantages still adds only one extra d20, which rollD20
 * enforces.
 */
export function resolveRollMode(
  advantageSources: number,
  disadvantageSources: number,
): RollMode {
  const net = advantageSources - disadvantageSources;
  if (net > 0) return 'advantage';
  if (net < 0) return 'disadvantage';
  return 'normal';
}

export interface D20Roll {
  mode: RollMode;
  /** Every d20 face rolled, in the order rolled. */
  dice: number[];
  /** The face actually used after applying the mode. */
  kept: number;
}

export function rollD20(roller: Roller, mode: RollMode = 'normal'): D20Roll {
  if (mode === 'normal') {
    const single = roller.roll(20);
    return { mode, dice: [single], kept: single };
  }
  const first = roller.roll(20);
  const second = roller.roll(20);
  const kept = mode === 'advantage' ? Math.max(first, second) : Math.min(first, second);
  return { mode, dice: [first, second], kept };
}
